import { AlertTriangle, Check, CalendarOff, Loader2, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { ClosureDatePicker } from '@/components/ClosureDatePicker';
import { Calendar } from '@/components/ui/calendar';
import { contactEmail } from '@/config/contact';
import { getPartner } from '@/data/partnersRepo';
import { useAvailabilitySlots } from '@/hooks/useAvailabilitySlots';
import { useExperienceClosures } from '@/hooks/useSuppliers';
import { useLogActivity } from '@/hooks/useTrackActivity';
import { slotTimeRangeLabel, type AvailabilitySlotType } from '@/types/types_availabilitySlot';
import {
  bookingRequestMaxDates,
  bookingRequestMaxRequestsLength,
  cleanPreferredDates,
  optionalServicesTotal,
  validateBookingRequest,
  type BookingRequestDraftType,
  type CreateBookingRequestResponseType,
} from '@/types/types_bookingRequest';
import { isDateClosed, weekdayClosureLines, type ExperienceClosuresType } from '@/types/types_closure';
import {
  isCalendarBookingType,
  normalizeOptionalServices,
  resolveOptionalServices,
  type ExperienceOptionalServiceType,
  type ExperienceType,
} from '@/types/types_experience';
import { callableErrorMessage, createBookingRequest } from '@/util/callable_functions';
import {
  formatCalendarDate,
  formatJpy,
  formatPrice,
  parseCalendarDate,
  toCalendarDate,
  todayCalendarDate,
} from '@/util/util_format';

interface InquiryFormCardProps {
  experience: ExperienceType;
}

/**
 * The booking panel: a partner's booking request for one of their guests.
 *
 * One component for both ways an experience is sold, because everything except
 * the question in the middle is the same: the guest's details, the guest count,
 * the special requests, the phone prefill, the submission and what is said
 * afterwards. What varies is *when* the artisan agreed.
 *
 *  - An **inquiry** asks for dates, in preference order, and lands as 'pending'
 *    for the desk to go and ask about. An exclusive experience turns on one
 *    artisan's diary, so alternatives are what let us say yes — the form says so
 *    plainly instead of leaving a concierge to guess.
 *  - A **direct** experience offers a calendar of slots the artisan has already
 *    agreed to work, and taking one confirms the booking on the spot. There is no
 *    "we will come back to you", because there is nobody to ask.
 *
 * Submitting goes through the `createBookingRequest` Cloud Function either way,
 * and it decides which of the two this is from the live experience rather than
 * from anything sent here. The partner it is attributed to comes from the caller's
 * own account, so nothing here needs to — or is allowed to — say which partner is
 * asking.
 *
 * Keyed on the experience by its parent, which is what clears one guest's
 * details when the concierge moves to the next experience — the route stays the
 * same and only its param changes, so nothing else would unmount this.
 */
export function InquiryFormCard({ experience }: InquiryFormCardProps) {
  const { partnerUser, isPartner } = useAuth();
  const logEvent = useLogActivity();
  const min = experience.minGuests ?? 1;
  const max = experience.maxGuests ?? min + 9;
  const guestOptions = Array.from({ length: Math.max(max - min + 1, 1) }, (_, i) => min + i);

  // Which question this panel asks. Off the experience, exactly as the Cloud
  // Function decides it — a panel offering a calendar the server will not book
  // from is worse than no calendar.
  // The experience's closures with its supplier's absences folded in. Read
  // rather than copied: a supplier holds the artisan's diary for every
  // experience they run, so a week added there closes this page's calendar
  // without anything being written onto the experience.
  const { closures, loading: closuresLoading } = useExperienceClosures(experience);
  const direct = isCalendarBookingType(experience.bookingType);
  // A direct booking is taken at the published price, so an experience priced on
  // request cannot be booked from here at all. The server refuses it; this says so
  // before the click, in the one place the concierge is looking.
  const priceOnRequest = direct && experience.basePrice <= 0;
  const { bookable: openSlots, loading: slotsLoading, error: slotsError } = useAvailabilitySlots(
    direct && !priceOnRequest ? experience.id : undefined,
  );
  // A slot on a day the artisan is since away for is not availability, whatever
  // the slot document still says. The CMS refuses to create one and flags any
  // that a later closure has covered; this is the other half — the concierge is
  // never offered a time the booking function would refuse.
  const bookable = openSlots.filter((slot) => !isDateClosed(slot.date, closures));

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  // Set once the partner's number has been folded in (or found missing), which
  // is what stops a late-arriving fetch from overwriting typing that beat it.
  const [phonePrefilled, setPhonePrefilled] = useState(false);
  const [dates, setDates] = useState<string[]>(['']);
  const [slotId, setSlotId] = useState('');
  const [guests, setGuests] = useState(min);
  // Nothing ticked, and nothing ticks itself later: an extra on a booking is one
  // the concierge asked for, since it is billed to their partner.
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [specialRequests, setSpecialRequests] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Set once the request is stored. Carries what the server did with it, which is
  // what the panel below reads back — a confirmed booking and a pending inquiry
  // are different pieces of news, and `notified: false` changes both of them.
  const [submitted, setSubmitted] = useState<CreateBookingRequestResponseType | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // The form runs several screens long, and what replaces it is a few lines — so
  // the news lands above the concierge who submitted from the bottom of it, with
  // the page left showing whatever follows the card. Put the card back in front
  // of them. After the render that swapped the form out, so the card has already
  // shrunk to the height being scrolled to.
  useEffect(() => {
    if (!submitted) return;
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [submitted]);

  const partnerId = partnerUser?.partnerId;

  // The chosen slot, looked up in the live list rather than trusted from state —
  // another partner can take it a second after it is picked, and the subscription
  // simply drops it. Deriving the selection this way means a slot that has gone
  // cannot be submitted, and `slotTaken` is what says so out loud instead of
  // letting the Confirm button fail on a time the concierge may already have
  // quoted to their guest.
  //
  // Not said while submitting or once submitted: the slot leaving the list is
  // then our own booking having taken it. The subscription sees that write before
  // the callable's promise resolves, so without the in-flight guard the warning
  // flashes up under the spinner a moment before the confirmation replaces it.
  const selectedSlot = bookable.find((slot) => slot.id === slotId) ?? null;
  const slotTaken = !!slotId && !selectedSlot && !slotsLoading && !submitting && !submitted;

  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    // Read straight through rather than via `usePartner`: that hook subscribes to
    // the whole collection, which the security rules only let an admin do — a
    // partner may read their own record and no other.
    getPartner(partnerId)
      .then((partner) => {
        if (cancelled) return;
        // Anything already typed is the concierge's and outranks the prefill,
        // same rule as the onboarding wizard's (`applyPartnerPrefill`).
        setPhone((current) => current || (partner?.phone ?? ''));
        setPhonePrefilled(true);
      })
      .catch((e) => {
        // A missing number is not worth an error in front of a guest — the field
        // simply stays empty and is filled in by hand.
        console.error('Could not prefill the partner phone number', e);
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  const showPhoneHint = phonePrefilled && !!phone;
  const offeredDates = cleanPreferredDates(dates);
  const today = todayCalendarDate();

  // The extras on offer, and the ones ticked — resolved against the experience
  // rather than kept as a second list, so a service withdrawn while the page was
  // open simply stops being selected instead of being submitted.
  const services = normalizeOptionalServices(experience.optionalServices);
  const selectedServices = resolveOptionalServices(services, serviceIds).services;

  function changeDate(index: number, value: string) {
    setDates((prev) => prev.map((d, i) => (i === index ? value : d)));
  }

  function addDate() {
    setDates((prev) => (prev.length < bookingRequestMaxDates ? [...prev, ''] : prev));
  }

  function removeDate(index: number) {
    setDates((prev) => prev.filter((_, i) => i !== index));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    // Not while the supplier's absences are still on their way: until they land,
    // the closures below are the experience's own, and the check would pass a
    // date the artisan is away for. The server would refuse it — this is what
    // stops the concierge finding that out the slow way.
    if (submitting || closuresLoading) return;

    const draft: BookingRequestDraftType = {
      experienceId: experience.id,
      customerFirstName: firstName,
      customerLastName: lastName,
      contactPhone: phone,
      // One or the other, never both: the server fills a direct booking's date in
      // from the slot it took, and dates typed into a calendar experience would be
      // preferences nobody reads.
      preferredDates: direct ? [] : offeredDates,
      slotId: direct ? selectedSlot?.id ?? '' : '',
      guests,
      // The ids alone. What each one costs is the experience's to say, and the
      // server prices them from its own copy of it.
      optionalServiceIds: selectedServices.map((service) => service.id),
      specialRequests,
    };

    // The same function the Cloud Function enforces with, so the answer here is
    // the answer the server would have given — just without the round trip.
    const problem = validateBookingRequest(draft, {
      minGuests: min,
      maxGuests: max,
      earliestDate: today,
      bookingType: experience.bookingType,
      closures,
      optionalServices: services,
    });
    if (problem) {
      setFormError(problem);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const result = await createBookingRequest(draft);
      setSubmitted(result.data);
      // Noted on the activity log as well as being a request in its own right,
      // so a partner's timeline shows what their browsing led to. Fire and forget:
      // the request is already made, and a failed metric must not report one.
      logEvent({
        action: 'bookingRequestSubmitted',
        path: window.location.pathname,
        targetId: experience.id,
      });
    } catch (e) {
      console.error('Submitting the booking request failed', e);
      setFormError(callableErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div ref={cardRef} id='inquiry' className='tw:scroll-mt-24 tw:bg-white tw:p-8 tw:shadow-xl tw:md:p-10'>
      <div className='tw:mb-8 tw:text-center'>
        <p className='tw:mb-2 tw:text-xs tw:tracking-[0.3em] tw:uppercase tw:text-ed-gold'>Reservations</p>
        <h3 className='tw:font-serif tw:text-3xl tw:text-ed-black'>
          {submitted
            ? submitted.status === 'confirmed'
              ? 'Booking Confirmed'
              : 'Request Received'
            : direct && !priceOnRequest
              ? 'Book a Time'
              : 'Inquire Now'}
        </h3>
        <p className='tw:mt-2 tw:text-sm tw:text-ed-stone'>
          {experience.basePrice <= 0
            ? 'Pricing available upon inquiry'
            // No "From" on a calendar booking: the slot is taken at this price, and
            // it is the figure the partner is committing to by pressing Confirm.
            : direct
              ? `${formatPrice(experience.basePrice)} per person`
              : `From ${formatPrice(experience.basePrice)} per person`}
        </p>
        {(experience.commissionEnabled || experience.famEnabled) && (
          <p className='tw:mt-3 tw:text-xs tw:tracking-wide tw:text-ed-umber'>
            {experience.commissionEnabled && `${experience.commissionPercent}% partner commission`}
            {experience.commissionEnabled && experience.famEnabled && ' · '}
            {experience.famEnabled && 'FAM trip available'}
          </p>
        )}
      </div>

      {submitted ? (
        <SubmittedPanel
          result={submitted}
          dates={offeredDates}
          onAnother={() => {
            setSubmitted(null);
            setFirstName('');
            setLastName('');
            setDates(['']);
            setSlotId('');
            setServiceIds([]);
            setSpecialRequests('');
            setFormError(null);
          }}
        />
      ) : priceOnRequest ? (
        // An experience sold off a calendar but priced on request has no price to
        // book at, so there is nothing here to press. Said plainly rather than
        // shown as an empty calendar, which would read as "no availability".
        <p className='tw:bg-ed-cream/60 tw:p-4 tw:text-center tw:text-sm tw:text-ed-charcoal/80'>
          This experience is priced individually. Please contact{' '}
          <a href={`mailto:${contactEmail}`} className='tw:text-ed-umber tw:underline tw:hover:text-ed-gold'>
            {contactEmail}
          </a>{' '}
          and we will arrange it for your guest.
        </p>
      ) : (
        <form className='tw:space-y-5' onSubmit={(e) => void submit(e)}>
          <div className='tw:grid tw:grid-cols-2 tw:gap-4'>
            <Field label='Customer First Name' required>
              <input
                type='text'
                name='firstName'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className='ed-input-elegant'
              />
            </Field>
            <Field label='Customer Last Name' required>
              <input
                type='text'
                name='lastName'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className='ed-input-elegant'
              />
            </Field>
          </div>

          <div>
            <Field label='Partner Contact Phone Number' required>
              <input
                type='tel'
                name='phone'
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className='ed-input-elegant'
              />
            </Field>
            {showPhoneHint && (
              <p className='tw:mt-2 tw:text-xs tw:text-ed-stone'>
                From your partner profile. Change it here if we should reach a different desk about this booking.
              </p>
            )}
          </div>

          <Field label='Guests'>
            <select
              name='guests'
              value={guests}
              onChange={(e) => setGuests(Number(e.target.value))}
              className='ed-input-elegant tw:bg-white'
            >
              {guestOptions.map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? 'Guest' : 'Guests'}
                </option>
              ))}
            </select>
          </Field>

          {/* The one question that differs between the two ways of selling: a
              calendar of times the artisan has agreed to, or a list of dates for
              us to go and ask about. */}
          {direct ? (
            <div>
              {/* Also shown here, where it explains an absence rather than
                  preventing one: a closure quietly removes the times on those
                  days from the list above. */}
              <ClosureNotice closures={closures} />
              <SlotPicker
                slots={bookable}
                loading={slotsLoading}
                error={slotsError}
                selectedId={selectedSlot?.id ?? ''}
                taken={slotTaken}
                onSelect={(id) => {
                  setSlotId(id);
                  setFormError(null);
                }}
              />
            </div>
          ) : (
          /* Preferred dates. A fieldset rather than a stack of loose inputs, so
             the group reads as one question to a screen reader — the legend is
             the question, and each row keeps its own label. */
          <fieldset>
            <legend className='tw:mb-2 tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>
              Preferred Dates *
            </legend>

            <ClosureNotice closures={closures} />

            <div className='tw:space-y-3'>
              {dates.map((value, i) => (
                // Index as key: the rows are interchangeable date inputs with no
                // identity of their own, and removal re-renders the whole list.
                <div key={i} className='tw:flex tw:items-center tw:gap-2'>
                  <div className='tw:flex-1'>
                    <ClosureDatePicker
                      value={value}
                      onChange={(v) => changeDate(i, v)}
                      closures={closures}
                      earliestDate={today}
                      ariaLabel={i === 0 ? 'First choice of date' : `Alternative date ${i}`}
                    />
                  </div>
                  {dates.length > 1 && (
                    <button
                      type='button'
                      onClick={() => removeDate(i)}
                      aria-label={i === 0 ? 'Remove first choice of date' : `Remove alternative date ${i}`}
                      className='tw:shrink-0 tw:p-3 tw:text-ed-stone tw:transition-colors tw:hover:text-ed-umber'
                    >
                      <X className='tw:size-4' />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {dates.length < bookingRequestMaxDates && (
              <button
                type='button'
                onClick={addDate}
                className='tw:mt-3 tw:flex tw:items-center tw:gap-2 tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-umber tw:transition-colors tw:hover:text-ed-gold'
              >
                <Plus className='tw:size-4' />
                <span>Add another date</span>
              </button>
            )}

            <p className='tw:mt-3 tw:text-xs tw:leading-relaxed tw:text-ed-stone'>
              {offeredDates.length > 1
                ? `${offeredDates.length} dates offered — thank you. We will confirm the first one the artisan can hold.`
                : 'Availability is subject to the artisan’s schedule. Selecting two or three preferred dates helps us confirm your experience more quickly and increases the likelihood of securing a suitable date.'}
            </p>
          </fieldset>
          )}

          {/* The extras this artisan offers, above the free-text box on purpose:
              a concierge who has just read "private shuttle" as a tick box does
              not then type it out as a request nobody has priced. */}
          {services.length > 0 && (
            <OptionalServicesPicker
              services={services}
              selectedIds={serviceIds}
              onToggle={(id, checked) =>
                setServiceIds((prev) => (checked ? [...prev, id] : prev.filter((s) => s !== id)))}
            />
          )}

          <Field label='Special Requests'>
            <textarea
              name='requests'
              rows={3}
              value={specialRequests}
              maxLength={bookingRequestMaxRequestsLength}
              placeholder='Any specific requests or questions…'
              onChange={(e) => setSpecialRequests(e.target.value)}
              className='ed-input-elegant tw:resize-none'
            />
          </Field>

          {/* What it comes to, last thing before the button: the total is what
              the concierge is committing their partner to, and for a party it is
              the per-person rate multiplied — not a sum to be done in the head
              off the "per person" line at the top of the panel. Everything above
              can change it, so it sits below all of it and reads as the answer.
              An experience priced on request has no rate to multiply and shows
              nothing here at all. */}
          {experience.basePrice > 0 && (
            <PriceSummary
              experience={experience}
              guests={guests}
              services={selectedServices}
              direct={direct}
            />
          )}

          {formError && (
            <p className='tw:border-l-4 tw:border-ed-red tw:bg-ed-red/5 tw:p-4 tw:text-sm tw:text-ed-red'>
              {formError}
            </p>
          )}

          {/* A request is a partner asking us to hold a date, billed and
              commissioned to that partner, so there is nothing for an Eighty Days
              admin browsing this page to submit. The server refuses either way;
              this says so before the click. */}
          {isPartner ? (
            <button
              type='submit'
              // Nothing to submit while the calendar has nothing on it. Not a
              // validation message: an empty calendar is not something the
              // concierge can put right by trying again.
              disabled={submitting || closuresLoading || (direct && bookable.length === 0)}
              aria-busy={submitting || undefined}
              className='tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-3 tw:bg-ed-gold tw:py-4 tw:text-sm tw:font-medium tw:tracking-widest tw:uppercase tw:text-ed-black tw:transition-all tw:duration-300 tw:hover:bg-ed-black tw:hover:text-ed-cream tw:disabled:cursor-not-allowed tw:disabled:opacity-60'
            >
              <span>{submitting ? 'Sending…' : direct ? 'Confirm Booking' : 'Submit Request'}</span>
              {submitting ? <Loader2 className='tw:size-5 tw:animate-spin' /> : <Check className='tw:size-5' />}
            </button>
          ) : (
            <p className='tw:bg-ed-cream/60 tw:p-4 tw:text-center tw:text-sm tw:text-ed-charcoal/80'>
              Booking requests are submitted from a partner account. Please contact{' '}
              <a href={`mailto:${contactEmail}`} className='tw:text-ed-umber tw:underline tw:hover:text-ed-gold'>
                {contactEmail}
              </a>{' '}
              to arrange this experience.
            </p>
          )}

          <p className='tw:text-center tw:text-xs tw:text-ed-stone'>
            {direct
              ? 'This time is held for your guest the moment you confirm, and we email you the details.'
              : 'We confirm availability with the artisan and reply to every request.'}
          </p>
        </form>
      )}
    </div>
  );
}

/**
 * The times on offer, as one radio group.
 *
 * Two shapes, one behind `slotListMaxDays`, because the same list is two
 * different problems at two different sizes.
 *
 *  - A handful of days is a **list**: every time visible at once, nothing to
 *    click before the answer is on screen. This is what an exclusive experience
 *    with three sittings in it wants, and a calendar there would hide three live
 *    days inside a month of dead ones.
 *  - A filled calendar is a **month grid**, then that day's times. It costs a
 *    click, and buys a picker whose height does not depend on how much the
 *    artisan has agreed to work — the shape a concierge already knows how to
 *    read, rather than a column they scroll past the Confirm button.
 *
 * Either way the days offered are only the ones that can actually be booked: a
 * day with nothing left on it is not selectable, here or in the grid, because a
 * date to click and be refused on is worse than a date that is plainly not there.
 *
 * `slots` are already filtered to the bookable ones and already in calendar
 * order, which is what lets this group them by reading straight down the list.
 */
function SlotPicker({
  slots,
  loading,
  error,
  selectedId,
  taken,
  onSelect,
}: {
  slots: AvailabilitySlotType[];
  loading: boolean;
  error: string | null;
  selectedId: string;
  /** The slot that was chosen has since been booked by somebody else. */
  taken: boolean;
  onSelect: (slotId: string) => void;
}) {
  const days = groupSlotsByDate(slots);
  // The day whose times are on screen in grid mode. Falls back to the day the
  // chosen time is on, so a selection made and then scrolled away from is still
  // the day being looked at — and so the grid opens on it after a re-render.
  const [pickedDate, setPickedDate] = useState('');
  const selectedSlot = slots.find((slot) => slot.id === selectedId) ?? null;
  const activeDate = pickedDate || selectedSlot?.date || '';

  return (
    <fieldset>
      <legend className='tw:mb-2 tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>
        Available Times *
      </legend>

      {error ? (
        <p className='tw:border-l-4 tw:border-ed-red tw:bg-ed-red/5 tw:p-4 tw:text-sm tw:text-ed-red'>{error}</p>
      ) : loading ? (
        <p className='tw:text-sm tw:text-ed-stone'>Loading availability…</p>
      ) : days.length === 0 ? (
        // An empty calendar is a real answer, and a different one from an error.
        // The artisan has nothing open rather than something we failed to load.
        <p className='tw:bg-ed-cream/60 tw:p-4 tw:text-sm tw:text-ed-charcoal/80'>
          There are no times open at the moment. Please write to{' '}
          <a href={`mailto:${contactEmail}`} className='tw:text-ed-umber tw:underline tw:hover:text-ed-gold'>
            {contactEmail}
          </a>{' '}
          and we will ask the artisan about other dates.
        </p>
      ) : (
        <div className='tw:space-y-4'>
          {taken && (
            <p className='tw:border-l-4 tw:border-ed-red tw:bg-ed-red/5 tw:p-4 tw:text-sm tw:text-ed-red'>
              The time you chose has just been taken. Please choose another.
            </p>
          )}
          {(
            <SlotCalendar
              days={days}
              activeDate={activeDate}
              selectedId={selectedId}
              onPickDate={(date) => {
                setPickedDate(date);
                // A time on another day must not stay chosen while this day's
                // are being read: the chip is then off screen, and the button at
                // the bottom of the panel would confirm a date nobody is looking
                // at.
                if (selectedSlot && selectedSlot.date !== date) onSelect('');
              }}
              onSelect={onSelect}
            />
          ) }

          <p className='tw:text-xs tw:leading-relaxed tw:text-ed-stone'>
            Each time is held for one party alone. Times shown in Japan Standard Time.
          </p>
        </div>
      )}
    </fieldset>
  );
}

/**
 * The month grid, and under it the times open on the chosen day.
 *
 * The same `Calendar` the inquiry form's date field uses, so a concierge who has
 * booked either way is reading one calendar. What differs is which days can be
 * chosen: here it is not "any day the artisan is not away" but the days that
 * actually have a time left on them, which is a live answer — another partner
 * taking the last slot on a day removes that day from the grid under the cursor.
 *
 * Paged between the first and last day with availability and no further. There
 * is nothing to find in March, and a calendar that lets you go looking implies
 * there might be.
 */
function SlotCalendar({
  days,
  activeDate,
  selectedId,
  onPickDate,
  onSelect,
}: {
  /** Days with times left on them, in calendar order. */
  days: [string, AvailabilitySlotType[]][];
  /** The day whose times are shown, `yyyy-mm-dd`, or '' for none chosen yet. */
  activeDate: string;
  selectedId: string;
  onPickDate: (date: string) => void;
  onSelect: (slotId: string) => void;
}) {
  const openDates = new Set(days.map(([date]) => date));
  const firstDay = parseCalendarDate(days[0][0]);
  const lastDay = parseCalendarDate(days[days.length - 1][0]);
  const selectedDay = parseCalendarDate(activeDate);

  // The chosen day's times, read out of the live list rather than remembered:
  // a day emptied by somebody else's booking while it is on screen becomes an
  // empty one, and says so, instead of offering times that have gone.
  const activeSlots = days.find(([date]) => date === activeDate)?.[1] ?? [];

  return (
    <div className='tw:space-y-4'>
      <Calendar
        className='ed-calendar-panel'
        mode='single'
        selected={selectedDay}
        defaultMonth={selectedDay ?? firstDay}
        startMonth={firstDay}
        endMonth={lastDay}
        // Days with no time left are not choices. Written as the inverse of the
        // open set so that everything else — past days, closed days, days the
        // artisan simply never offered — is refused by one rule.
        disabled={(day) => !openDates.has(toCalendarDate(day))}
        modifiers={{ open: (day) => openDates.has(toCalendarDate(day)) }}
        modifiersClassNames={{ open: 'ed-day-open' }}
        onSelect={(day) => {
          if (day) onPickDate(toCalendarDate(day));
        }}
      />

      {activeDate && activeSlots.length > 0 ? (
        <div>
          <p className='tw:mb-2 tw:text-sm tw:text-ed-black'>{formatCalendarDate(activeDate)}</p>
          <SlotTimeChips slots={activeSlots} selectedId={selectedId} onSelect={onSelect} />
        </div>
      ) : (
        <p className='tw:text-sm tw:text-ed-stone'>
          {activeDate
            ? 'The times on that day have just been taken. Please choose another.'
            : 'Choose a highlighted date to see the times open on it.'}
        </p>
      )}
    </div>
  );
}

/**
 * One day's times, as the radio group itself.
 *
 * The radios carry one `name` across the whole picker, which is what makes the
 * choice exclusive even in grid mode, where the times of another day are not
 * rendered at all — the browser keeps the group's selection, and the parent
 * clears it deliberately when the day changes rather than leaving an invisible
 * chip checked.
 */
function SlotTimeChips({
  slots,
  selectedId,
  onSelect,
}: {
  slots: AvailabilitySlotType[];
  selectedId: string;
  onSelect: (slotId: string) => void;
}) {
  return (
    <div className='tw:flex tw:flex-wrap tw:gap-2'>
      {slots.map((slot) => (
        <label
          key={slot.id}
          className={`tw:cursor-pointer tw:border tw:px-4 tw:py-2 tw:text-sm tw:transition-colors ${
            selectedId === slot.id
              ? 'tw:border-ed-gold tw:bg-ed-gold/15 tw:text-ed-black'
              : 'tw:border-ed-umber/30 tw:text-ed-charcoal/80 tw:hover:border-ed-gold'
          }`}
        >
          <input
            type='radio'
            name='slotId'
            value={slot.id}
            checked={selectedId === slot.id}
            onChange={() => onSelect(slot.id)}
            className='tw:sr-only'
          />
          {slotTimeRangeLabel(slot)}
        </label>
      ))}
    </div>
  );
}

/**
 * What the booking comes to, itemised: the base rate for the party, every extra
 * ticked, and the total underneath.
 *
 * The base rate is written out as the sum it is — "2 guests × ¥38,000" — because
 * that is the arithmetic a concierge would otherwise do by hand off the "per
 * person" line at the top of the panel, with their guest on the phone. The rate
 * is per person and the extras are not: a shuttle is one shuttle whoever is in
 * it, which is why only the first row is multiplied.
 *
 * On a direct booking this is the figure being committed to, and the same rows
 * the Cloud Function prices the confirmation from. On an inquiry it is the
 * catalogue arithmetic and nothing has been agreed yet, so it says so rather
 * than reading as a quote from us.
 */
function PriceSummary({
  experience,
  guests,
  services,
  direct,
}: {
  experience: ExperienceType;
  guests: number;
  /** The extras actually ticked, in the order they are offered. */
  services: ExperienceOptionalServiceType[];
  direct: boolean;
}) {
  const baseTotal = experience.basePrice * guests;
  const total = baseTotal + optionalServicesTotal(services);

  return (
    <div className='tw:border tw:border-ed-umber/20 tw:bg-ed-cream/40 tw:p-4'>
      <div className='tw:space-y-2 tw:text-sm'>
        <div className='tw:flex tw:items-baseline tw:justify-between tw:gap-4'>
          <span className='tw:text-ed-charcoal/80'>
            Base Rate ({guests} {guests === 1 ? 'guest' : 'guests'} &times; {formatJpy(experience.basePrice)})
          </span>
          <span className='tw:shrink-0 tw:text-ed-black'>{formatJpy(baseTotal)}</span>
        </div>

        {services.map((service) => (
          <div key={service.id} className='tw:flex tw:items-baseline tw:justify-between tw:gap-4'>
            <span className='tw:text-ed-charcoal/80'>{service.label}</span>
            {/* Same wording as the tick box it came from — a row of "¥0" here
                would read as a price we forgot to fill in. */}
            <span className='tw:shrink-0 tw:text-ed-black'>
              {service.price > 0 ? formatJpy(service.price) : 'No charge'}
            </span>
          </div>
        ))}
      </div>

      <div className='tw:mt-3 tw:flex tw:items-baseline tw:justify-between tw:gap-4 tw:border-t tw:border-ed-umber/20 tw:pt-3'>
        <span className='tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>Total</span>
        <span className='tw:font-serif tw:text-xl tw:text-ed-black'>{formatJpy(total)}</span>
      </div>

      {!direct && (
        <p className='tw:mt-2 tw:text-xs tw:leading-relaxed tw:text-ed-stone'>
          Indicative, at the published rate. We confirm the final price when we reply.
        </p>
      )}
    </div>
  );
}

/**
 * The extras this experience can be arranged with — a car, an interpreter, a
 * lunch — as a list of tick boxes with what each one adds.
 *
 * All of them start unticked, and that is the whole design rather than a
 * default: every line here is money on the partner's invoice, so a service on a
 * booking has to be one somebody chose. Nothing is pre-selected, nothing is
 * "recommended", and an experience with no services shows no list at all.
 *
 * The price is on the row rather than in a summary underneath, because it is
 * what the concierge is deciding about — and stated as what it *adds*, since the
 * experience's own price is already at the top of this panel.
 */
function OptionalServicesPicker({
  services,
  selectedIds,
  onToggle,
}: {
  services: ExperienceOptionalServiceType[];
  selectedIds: string[];
  onToggle: (serviceId: string, checked: boolean) => void;
}) {
  return (
    <fieldset>
      <legend className='tw:mb-2 tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>
        Additional Services
      </legend>

      <div className='tw:space-y-2'>
        {services.map((service) => (
          <label
            key={service.id}
            className='tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:border tw:border-ed-umber/20 tw:px-4 tw:py-3 tw:text-sm tw:transition-colors tw:hover:border-ed-gold'
          >
            <input
              type='checkbox'
              name='optionalServices'
              value={service.id}
              checked={selectedIds.includes(service.id)}
              onChange={(e) => onToggle(service.id, e.target.checked)}
              className='tw:size-4 tw:shrink-0 tw:accent-ed-gold'
            />
            <span className='tw:flex-1 tw:text-ed-black'>{service.label}</span>
            {/* A service we arrange at no charge is said so rather than shown as
                "+¥0", which reads as a price we forgot to fill in. */}
            <span className='tw:shrink-0 tw:text-ed-charcoal/80'>
              {service.price > 0 ? `+${formatJpy(service.price)}` : 'No charge'}
            </span>
          </label>
        ))}
      </div>

      <p className='tw:mt-3 tw:text-xs tw:leading-relaxed tw:text-ed-stone'>
        Optional. Anything you tick is arranged with the experience and added to the total below.
      </p>
    </fieldset>
  );
}

/** The slots as `[date, slots]` pairs, in calendar order. Input is already sorted. */
function groupSlotsByDate(slots: AvailabilitySlotType[]): [string, AvailabilitySlotType[]][] {
  const days = new Map<string, AvailabilitySlotType[]>();
  for (const slot of slots) {
    const day = days.get(slot.date);
    if (day) day.push(slot);
    else days.set(slot.date, [slot]);
  }
  return [...days];
}

/**
 * What the concierge sees once the booking is stored.
 *
 * Two pieces of news, and the difference matters: an inquiry is now out of their
 * hands and reads back the dates they offered, so a wrong one can still be
 * noticed; a direct booking is *done*, and reads back the time their guest is
 * expected at. Neither is a bare "thank you".
 */
function SubmittedPanel({
  result,
  dates,
  onAnother,
}: {
  result: CreateBookingRequestResponseType;
  dates: string[];
  onAnother: () => void;
}) {
  // Absent from a function deployed before direct booking existed, in which case
  // it can only have been an inquiry. See the field.
  const confirmed = result.status === 'confirmed';
  const time = slotTimeRangeLabel({
    startTime: result.confirmedStartTime ?? '',
    endTime: result.confirmedEndTime ?? '',
  });

  return (
    <div className='tw:space-y-5'>
      <div className='tw:flex tw:items-start tw:gap-3 tw:border-l-4 tw:border-ed-gold tw:bg-ed-cream/50 tw:p-4'>
        <Check className='tw:mt-0.5 tw:size-5 tw:shrink-0 tw:text-ed-gold' />
        <div className='tw:text-sm tw:leading-relaxed tw:text-ed-charcoal/80'>
          {confirmed ? (
            <p>
              Your guest&rsquo;s booking is confirmed for{' '}
              <span className='tw:text-ed-black'>
                {formatCalendarDate(result.confirmedDate)}
                {time && `, ${time}`}
              </span>
              . We have emailed you the details, including the cancellation terms.
            </p>
          ) : (
            <p>Your request is with our product desk. We will confirm which date the artisan can hold.</p>
          )}
          {/* The request code where the booking was given one, and the first
              characters of the id where it was not — which is what this line
              showed before codes existed. Never the whole id: it is twenty
              characters of noise to read out over a phone, and the desk can find
              a booking from the first eight. */}
          <p className='tw:mt-2 tw:text-xs tw:text-ed-stone'>
            Reference {result.requestCode || result.requestId.slice(0, 8)}
          </p>
        </div>
      </div>

      {!confirmed && (
        <div>
          <p className='tw:mb-2 tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>Dates you offered</p>
          <ul className='tw:space-y-1 tw:text-sm tw:text-ed-charcoal/80'>
            {dates.map((date, i) => (
              <li key={date}>
                {formatCalendarDate(date)}
                {i === 0 && dates.length > 1 && (
                  <span className='tw:ml-2 tw:text-xs tw:text-ed-stone'>first choice</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The booking is safely stored — but if the desk was not emailed, nobody
          may look at it today, and the concierge is the only one who can tell.
          On a confirmed booking that is worse than a delay: the artisan is the
          one who has not been told, and a guest is expected. */}
      {!result.notified && (
        <div className='tw:flex tw:items-start tw:gap-3 tw:border-l-4 tw:border-ed-red tw:bg-ed-red/5 tw:p-4 tw:text-sm tw:text-ed-charcoal/80'>
          <AlertTriangle className='tw:mt-0.5 tw:size-5 tw:shrink-0 tw:text-ed-red' />
          <p>
            {confirmed
              ? 'Your booking is confirmed, but the email telling our desk about it did not send. Please write to '
              : 'Your request is recorded, but our notification email did not send. If this is urgent, please also write to '}
            <a href={`mailto:${contactEmail}`} className='tw:text-ed-umber tw:underline tw:hover:text-ed-gold'>
              {contactEmail}
            </a>
            {confirmed ? ' quoting the reference above, so we can confirm it with the artisan.' : '.'}
          </p>
        </div>
      )}

      {/* Separate from the above: the desk knowing and the partner having it in
          writing are different failures, and only this one leaves the concierge
          without a record to forward. */}
      {result.confirmationSent === false && (
        <div className='tw:flex tw:items-start tw:gap-3 tw:border-l-4 tw:border-ed-red tw:bg-ed-red/5 tw:p-4 tw:text-sm tw:text-ed-charcoal/80'>
          <AlertTriangle className='tw:mt-0.5 tw:size-5 tw:shrink-0 tw:text-ed-red' />
          <p>
            We could not email you the written confirmation. The booking is made — please write to{' '}
            <a href={`mailto:${contactEmail}`} className='tw:text-ed-umber tw:underline tw:hover:text-ed-gold'>
              {contactEmail}
            </a>{' '}
            if you need it in writing for your guest.
          </p>
        </div>
      )}

      <button
        type='button'
        onClick={onAnother}
        className='tw:w-full tw:border tw:border-ed-umber/30 tw:py-3 tw:text-xs tw:tracking-widest tw:uppercase tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-gold tw:hover:text-ed-gold'
      >
        {confirmed ? 'Book this experience for another guest' : 'Request this experience for another guest'}
      </button>
    </div>
  );
}

/**
 * The days of the week this experience never runs, stated before the concierge
 * starts choosing.
 *
 * The weekly rules only. A standing arrangement — "closed every Monday" — is
 * something a concierge needs in order to know where to look at all, and no
 * calendar can say it: paging through months to discover that every Monday is
 * struck out is not how anybody finds that out.
 *
 * The artisan's dated absences are deliberately not here. They belong to
 * particular days, and the picker already says so on the days themselves, in
 * red, with the reason. Listing them again above turned a card into a notice
 * board that grew every time somebody booked a holiday.
 *
 * Absent entirely when nothing is closed weekly, so an ordinary experience gains
 * no furniture.
 */
function ClosureNotice({ closures }: { closures: ExperienceClosuresType }) {
  const lines = weekdayClosureLines(closures);
  if (lines.length === 0) return null;

  return (
    <div className='tw:mb-3 tw:flex tw:items-start tw:gap-2.5 tw:border-l-4 tw:border-ed-umber/40 tw:bg-ed-cream/60 tw:p-3'>
      <CalendarOff className='tw:mt-0.5 tw:size-4 tw:shrink-0 tw:text-ed-umber' />
      <div className='tw:text-xs tw:leading-relaxed tw:text-ed-charcoal/80'>
        <p className='tw:text-ed-black'>This experience does not run on</p>
        <ul className='tw:mt-1 tw:space-y-0.5'>
          {lines.map((line) => <li key={line}>{line}</li>)}
        </ul>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className='tw:block'>
      <span className='tw:mb-2 tw:block tw:text-xs tw:tracking-wider tw:uppercase tw:text-ed-stone'>
        {label} {required && '*'}
      </span>
      {children}
    </label>
  );
}
